import { Outlet, useParams, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'
import { SUPPORTED_LOCALES, type Locale } from '@/env'
import { setLocale, detectedLocale } from '@/lib/i18n'
import { useAuth } from '@/lib/auth'
import { takePostLoginRedirect } from '@/lib/postLoginRedirect'

/**
 * Wraps every localized route. Validates the :locale URL segment against our
 * 9 supported locales (redirect on unknown) and syncs i18next + <html lang> to it.
 */
export function LocaleGate() {
  const params = useParams<{ locale: string }>()
  const localeParam = params.locale
  const { i18n } = useTranslation()
  const { session } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const supported = SUPPORTED_LOCALES as readonly string[]
  const isValid = !!localeParam && supported.includes(localeParam)
  const lc = (isValid ? (localeParam as Locale) : null) as Locale | null

  useEffect(() => {
    if (!lc) return
    i18n.changeLanguage(lc)
    setLocale(lc)
    document.documentElement.lang = lc
  }, [lc, i18n])

  // Native-feeling navigation: reset scroll when the localized path changes
  // (no ScrollRestoration equivalent in data-router DOM mode for this setup).
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  // A magic-link sign-in lands on the locale root; if the user was bounced to
  // login from somewhere specific, send them back there now.
  useEffect(() => {
    if (!session) return
    const target = takePostLoginRedirect()
    if (target && target !== `${location.pathname}${location.search}`) navigate(target, { replace: true })
    // Only react to auth becoming available, not every route change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  if (!isValid) return <Navigate to={`/${detectedLocale()}`} replace />
  return <Outlet />
}